import React, { useRef } from "react";
import { IconX } from "@tabler/icons-react";
import { styled } from "@mui/material";
import { I_GENERIC_ITEMS } from "@/src-refactor/modules/shared/model/interface/items.interface";
import { useOverflow } from "./useOverflow";

interface SelectedChipsProps {
  /** Prefijo de ID para los data-testid */
  id?: string;
  /** Elementos seleccionados */
  items: I_GENERIC_ITEMS[];
  /** Callback para eliminar chip */
  onRemove(optionId: string | number): void;
}

const ChipsWrapper = styled("div")({
  display: "flex",
  flexWrap: "nowrap",
  alignItems: "center",
  overflow: "hidden",
  flex: 1,
  gap: "4px",
  whiteSpace: "nowrap"
});

const ChipContainer = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  background: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  borderRadius: 20,
  padding: "4px 10px",
  marginRight: "4px",
  fontSize: "12px",
  "& .remove": {
    marginLeft: "4px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    "&:hover": {
      color: theme.palette.error.main
    }
  }
}));

/**
 * Renderiza los chips seleccionados. Se encarga de:
 * 1. Controlar si hay overflow de ancho para mostrar "...".
 * 2. Limitar visualmente a los primeros 2 elementos.
 * 3. Delegar la eliminación al callback recibido.
 */
const SelectedChips: React.FC<SelectedChipsProps> = ({
  id = "",
  items,
  onRemove
}) => {
  const chipsWrapperRef = useRef<HTMLDivElement>(null);
  const isOverflowing = useOverflow(chipsWrapperRef, [items]);

  const displayedItems = items.slice(0, 2);

  return (
    <ChipsWrapper
      ref={chipsWrapperRef}
      id={`${id}-chips-wrapper`}
      data-testid={`${id}-chips-wrapper`}
    >
      {displayedItems.map(item => (
        <ChipContainer
          key={`${item.value}-${item.label}`}
          id={`${id}-chip-${item.value}`}
          data-testid={`${id}-chip-${item.value}`}
        >
          {item.label}
          <span
            className="remove"
            id={`${id}-chip-remove-${item.value}`}
            data-testid={`${id}-chip-remove-${item.value}`}
            onClick={e => {
              e.stopPropagation();
              onRemove(item.value);
            }}
          >
            <IconX size={12} />
          </span>
        </ChipContainer>
      ))}
      {(isOverflowing || items.length > 2) && (
        <span
          style={{ fontSize: "12px" }}
          id={`${id}-chip-overflow`}
          data-testid={`${id}-chip-overflow`}
        >
          ...
        </span>
      )}
    </ChipsWrapper>
  );
};

export default React.memo(SelectedChips);
